import { Stack, ThemeProvider } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";

interface Props {
  modalState: UseModalState;
  onConfirm: () => Promise<void>;
  onLogout: () => Promise<void> | void;
  onContactSupport: () => Promise<void> | void;
  isReactivating: boolean;
  isLoggingOut: boolean;
}

export function ReactivationRequiredDialog(props: Props) {
  const { modalState, onConfirm, onLogout, onContactSupport, isReactivating, isLoggingOut } = props;
  const theme = getTheme();
  const isBusy = isReactivating || isLoggingOut;

  return (
    <ThemeProvider theme={theme}>
      <BottomSheet modalState={modalState}>
        <BottomSheetIllustrationContent
          illustrationType="warning"
          title="Your account was deactivated"
          description="This phone number is linked to a Clipboard account that was deleted. Verify your identity to reactivate it, or log out to sign in with a different number."
        />
        <DialogFooter orientation="vertical">
          <Stack direction="column" spacing={6} width="100%">
            <Stack direction="column" spacing={3} width="100%">
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                isLoading={isReactivating}
                disabled={isBusy && !isReactivating}
                aria-label="Reactivate my account"
                onClick={async () => {
                  await onConfirm();
                  modalState.closeModal();
                }}
              >
                Reactivate my account
              </LoadingButton>
              <LoadingButton
                fullWidth
                variant="outlined"
                size="large"
                isLoading={isLoggingOut}
                disabled={isBusy && !isLoggingOut}
                aria-label="Log out"
                onClick={async () => {
                  await onLogout();
                }}
              >
                Log out
              </LoadingButton>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              alignItems="center"
              flexWrap="wrap"
            >
              <Text semibold sx={{ color: theme.palette.text?.secondary }}>
                This wasn&apos;t me.
              </Text>
              <TextButton
                disabled={isBusy}
                aria-label="Contact support"
                onClick={async () => {
                  await onContactSupport();
                }}
              >
                <Text semibold sx={{ color: theme.palette.link?.primary }}>
                  Contact support
                </Text>
              </TextButton>
            </Stack>
          </Stack>
        </DialogFooter>
      </BottomSheet>
    </ThemeProvider>
  );
}
